/**
 * Первая нога зашла, вторая ушла: чем закрыть с минимальным минусом.
 *
 * Это самая частая настоящая беда вилочника, и ни один сканер на неё не
 * отвечает. Сканеры считают вилку и замолкают ровно в тот момент, когда человек
 * уже отдал деньги и остался с половиной позиции: цена второй ноги уехала, пока
 * он вбивал сумму в первую. Дальше он считает в голове — быстро, под давлением
 * и с чужими деньгами на кону.
 *
 * Что здесь считается и почему именно так.
 *
 * **Закрыть — значит уравнять выплаты.** Ставка второй ноги подбирается так,
 * чтобы возврат был одинаков при любом исходе: тогда результат известен сразу и
 * не зависит от матча. При ушедшей цене этот результат бывает отрицательным — и
 * это правильный ответ, а не повод его прятать. Гарантированный минус меньше
 * возможного: закрытие всегда теряет меньше, чем полная ставка (доказательство
 * простое — доливать пришлось бы больше первой выплаты только при кэфе ниже
 * единицы, а таких не бывает).
 *
 * **Граница называется числом.** Кэф, при котором вторая нога ещё выводит в
 * ноль, — это `o1 / (o1 − 1)`. Человеку он нужнее любых советов: по нему видно,
 * стоит ли ждать возврата цены или закрывать сейчас.
 *
 * **Ничего не делать — тоже вариант, и его цифры показываются.** Оставить одну
 * ногу значит поставить обычную ставку: либо `+s(o1−1)`, либо `−s`. Скрыть эту
 * пару и оставить только «закройтесь» значило бы принимать решение за человека.
 *
 * Decimal, а не number: числа отсюда человек сверяет со своим калькулятором,
 * держа открытую позицию, и расхождение в третьем знаке читается как «сканер
 * врёт» — ровно в тот момент, когда доверие дороже всего.
 */

import { Decimal } from 'decimal.js';

export interface Rescue {
  /** Ставка на вторую ногу, которая уравнивает выплаты. */
  stake: Decimal;
  /** Сколько всего окажется вложено вместе с первой ногой. */
  total: Decimal;
  /** Возврат при любом исходе после закрытия. */
  payout: Decimal;
  /** Итог закрытия: отрицательный — это цена ошибки, а не поломка расчёта. */
  result: Decimal;
  /** Кэф второй ноги, при котором закрытие выходит в ноль. */
  breakEven: Decimal;
  /** Догнала ли цена: закрытие всё ещё в плюс. */
  profitable: boolean;
  /** Если не закрывать: выигрыш первой ноги и проигрыш первой ноги. */
  openWin: Decimal;
  openLoss: Decimal;
}

const d = (value: Decimal | string | number): Decimal => new Decimal(value);

/**
 * Расчёт закрытия. `stake` и `odds` — первая, уже поставленная нога; `now` —
 * кэф второй ноги прямо сейчас.
 *
 * Бросает при негодных входных: кэф не выше единицы или ставка не выше нуля —
 * это не «край случая», а испорченные данные, и посчитать по ним что-нибудь
 * правдоподобное было бы хуже отказа. Число из этого расчёта попадает в решение
 * о живых деньгах.
 */
export function rescue(
  stake: Decimal | string | number,
  odds: Decimal | string | number,
  now: Decimal | string | number,
): Rescue {
  const s = d(stake);
  const o1 = d(odds);
  const o2 = d(now);
  if (s.lte(0)) throw new Error('ставка первой ноги должна быть положительной');
  if (o1.lte(1) || o2.lte(1)) throw new Error('кэф должен быть больше единицы');

  const target = s.times(o1);
  const second = target.div(o2);
  const total = s.plus(second);
  return {
    stake: second,
    total,
    payout: target,
    result: target.minus(total),
    breakEven: o1.div(o1.minus(1)),
    profitable: target.gt(total),
    openWin: s.times(o1.minus(1)),
    openLoss: s.negated(),
  };
}
